//* Libraries imports
import { motion } from 'framer-motion';

//? Styles imports
import './resource-list-skeleton.scss';

interface ResourceListSkeletonProps {
  showTable?: boolean;
}

export function ResourceListSkeleton({ showTable = true }: ResourceListSkeletonProps) {
  const skeletonVariants = {
    pulse: {
      opacity: [0.6, 1, 0.6],
      transition: {
        duration: 1.5,
        repeat: Infinity
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  // If showTable is false, show the "in-progress" status skeleton
  if (!showTable) {
    return (
      <motion.div
        className="resource-list-skeleton w-100"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="d-flex justify-content-center align-items-center min-vh-100 w-100">
          <div className="process-status-skeleton">
            <motion.div
              className="skeleton-status-icon mb-4"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            <motion.div
              className="skeleton-status-title mb-3"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            <motion.div
              className="skeleton-status-subtitle mb-4"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            <motion.div
              className="skeleton-status-message mb-4"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            <motion.div
              className="skeleton-status-button"
              variants={skeletonVariants}
              animate="pulse"
            />
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="resource-list-skeleton w-100"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header Section Skeleton */}
      <motion.section 
        className="resource-header-skeleton p-4"
        variants={itemVariants}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <motion.div
              className="skeleton-back-button me-3"
              variants={skeletonVariants}
              animate="pulse"
            />
            <div>
              <motion.div
                className="skeleton-process-title mb-2"
                variants={skeletonVariants}
                animate="pulse"
              />
              <motion.div
                className="skeleton-process-info"
                variants={skeletonVariants}
                animate="pulse"
              />
            </div>
          </div>
          {/* Status Badge Skeleton */}
          <motion.div
            className="skeleton-status-badge"
            variants={skeletonVariants}
            animate="pulse"
          />
        </div>
        
        {/* Info Card Skeleton */}
        <motion.div
          className="skeleton-info-card"
          variants={skeletonVariants}
          animate="pulse"
        />
      </motion.section>

      {/* Resource Card Section Skeleton */}
      <motion.section 
        className="resource-card-skeleton p-4"
        variants={itemVariants}
      >
        <div className="row g-3">
          <div className="col-12 col-md-6 col-lg-4">
            <motion.div
              className="skeleton-resource-card"
              variants={skeletonVariants}
              animate="pulse"
            >
              <div className="d-flex align-items-center mb-3">
                <motion.div
                  className="skeleton-card-icon me-3"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-card-title"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>
              <motion.div
                className="skeleton-card-description mb-3"
                variants={skeletonVariants}
                animate="pulse"
              />
              <motion.div
                className="skeleton-card-description-short"
                variants={skeletonVariants}
                animate="pulse"
              />
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Table Section Skeleton */}
      <motion.section 
        className="resource-table-skeleton p-5"
        variants={itemVariants}
      >
        <div className="container bg-white p-4 rounded-3 shadow-sm">
          {/* Table Header Skeleton */}
          <motion.div
            className="skeleton-table-title mb-4"
            variants={skeletonVariants}
            animate="pulse"
          />
          
          {/* Search Input Skeleton */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <motion.div
              className="skeleton-search-input"
              variants={skeletonVariants}
              animate="pulse"
            />
            <motion.div
              className="skeleton-filter-button"
              variants={skeletonVariants}
              animate="pulse"
            />
          </div>

          {/* Table Content Skeleton */}
          <div className="skeleton-table">
            {/* Table Head */}
            <div className="skeleton-table-row skeleton-table-head">
              <div className="skeleton-table-cell skeleton-table-cell-header"></div>
              <div className="skeleton-table-cell skeleton-table-cell-header"></div>
              <div className="skeleton-table-cell skeleton-table-cell-header"></div>
              <div className="skeleton-table-cell skeleton-table-cell-header"></div>
              <div className="skeleton-table-cell skeleton-table-cell-header"></div>
            </div>
            
            {/* Table Body */}
            {[...Array(5)].map((_, rowIndex) => (
              <motion.div
                key={rowIndex}
                className="skeleton-table-row"
                variants={skeletonVariants}
                animate="pulse"
                style={{ animationDelay: `${rowIndex * 0.1}s` }}
              >
                <div className="skeleton-table-cell">
                  <div className="skeleton-table-cell-content"></div>
                </div>
                <div className="skeleton-table-cell">
                  <div className="skeleton-table-cell-content"></div>
                </div>
                <div className="skeleton-table-cell">
                  <div className="skeleton-table-cell-content"></div>
                </div>
                <div className="skeleton-table-cell">
                  <div className="skeleton-table-cell-content skeleton-table-cell-content-short"></div>
                </div>
                <div className="skeleton-table-cell">
                  <div className="skeleton-table-cell-actions">
                    <div className="skeleton-action-button"></div>
                    <div className="skeleton-action-button"></div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pagination Section */}
          <div className="skeleton-pagination-container mt-4">
            <div className="d-flex justify-content-between align-items-center">
              <motion.div
                className="skeleton-pagination-info"
                variants={skeletonVariants}
                animate="pulse"
              />
              <div className="d-flex">
                {[...Array(5)].map((_, index) => (
                  <motion.div
                    key={index}
                    className="skeleton-pagination-button me-2"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.section>
    </motion.div>
  );
}
